import React, {
    useState,
    useRef,
    useEffect,
    cloneElement,
    ReactElement,
} from 'react'
import { createPortal } from 'react-dom'

interface HoverTooltipProps {
    children: ReactElement
    tooltipText?: string
    tooltipContent?: React.ReactNode
    offsetX?: number
    offsetY?: number
    delay?: number
    align?: 'left' | 'center' | 'right'
    valign?: 'top' | 'middle' | 'bottom'
}

const HoverTooltip: React.FC<HoverTooltipProps> = ({
    children,
    tooltipText,
    tooltipContent,
    offsetX = 10,
    offsetY = 10,
    delay = 100,
    align = 'right',
    valign = 'bottom',
}) => {
    const [isVisible, setIsVisible] = useState(false)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const timeoutRef = useRef<number | null>(null)
    const tooltipRef = useRef<HTMLDivElement | null>(null)

    const hasContent =
        (typeof tooltipContent !== 'undefined' && tooltipContent !== null) ||
        (tooltipText?.trim?.() ?? '') !== ''

    const handleMouseEnter = () => {
        if (!hasContent) return

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = window.setTimeout(() => {
            setIsVisible(true)
        }, delay)
    }

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!hasContent) return

        const w = tooltipRef.current?.offsetWidth ?? 0
        const h = tooltipRef.current?.offsetHeight ?? 0

        let x = e.clientX
        if (align === 'left') x = e.clientX - offsetX - w
        else if (align === 'center') x = e.clientX - w / 2
        else x = e.clientX + offsetX

        let y = e.clientY
        if (valign === 'top') y = e.clientY - offsetY - h
        else if (valign === 'middle') y = e.clientY - h / 2
        else y = e.clientY + offsetY

        setPosition({ x, y })
    }

    const handleMouseLeave = () => {
        if (!hasContent) return

        setIsVisible(false)

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const childWithEvents = cloneElement(children, {
        onMouseEnter: (e: React.MouseEvent) => {
            handleMouseEnter()
            children.props.onMouseEnter?.(e)
        },
        onMouseMove: (e: React.MouseEvent) => {
            handleMouseMove(e)
            children.props.onMouseMove?.(e)
        },
        onMouseLeave: (e: React.MouseEvent) => {
            handleMouseLeave()
            children.props.onMouseLeave?.(e)
        },
    })

    return (
        <>
            {childWithEvents}

            {typeof document !== 'undefined' &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className="fixed max-w-[500px] z-50 px-1.5 py-1.5 bg-[#d9dce0] dark:bg-[#2b2a33] border border-[#6A6A6A] dark:border-[#8A8A8A] text-[#181818] dark:text-[#FFFFFF] text-[12.8px] rounded shadow-md pointer-events-none transition-opacity duration-200 ease-in-out"
                        style={{
                            left: `${position.x}px`,
                            top: `${position.y}px`,
                            opacity: isVisible ? 1 : 0,
                            visibility: isVisible ? 'visible' : 'hidden',
                        }}
                    >
                        {tooltipContent ?? tooltipText}
                    </div>,
                    document.body
                )}
        </>
    )
}

export default HoverTooltip
